import { type IMobileApplications } from "@next-platform/client-common-core";
import type { TFunction } from "i18next";

import {
  AndroidDark,
  AndroidLight,
  APPLICATIONS_ID,
  APPLICATIONS_NAME,
  AppStoreDark,
  AppStoreLight,
  GetItOnPlayStoreDark,
  GetItOnPlayStoreLight,
  ROUTES,
} from "@/constants";
import { ANDROID_PLATFORM_OS, IOS_PLATFORM_OS, type MobileApplicationPlatform } from "@/features/mobileApplications/constants";
import { type BrandedMobileApplication } from "@/features/mobileApplications/getBrandedMobileApplications";

type ApplicationDownloadLink = {
  platform: MobileApplicationPlatform;
  badgeImage: string;
  redirectLink: string;
  target: "_blank" | "_self";
};

type ApplicationCardDetails = {
  id: string;
  logo?: string;
  backgroundImage?: string;
  applicationName: string;
  title: string;
  description: string;
  features: string[];
  note?: string;
  backgroundColor: string;
  downloadLinks: ApplicationDownloadLink[];
};

const getSupportedAppsHeaderDetails = ({
  applicationId,
  t,
  applicationName,
}: {
  applicationId: string;
  t: TFunction;
  applicationName: string | undefined;
}) => {
  const headersByApp: Partial<Record<string, { title: string; description: string }>> = {
    [APPLICATIONS_ID.SpicyChat]: {
      title: t("appInstall:supportedApps.header.spicychat.title"),
      description: t("appInstall:supportedApps.header.spicychat.description", { appName: applicationName }),
    },
    [APPLICATIONS_ID.PixelChat]: {
      title: t("appInstall:supportedApps.header.pixelchat.title"),
      description: t("appInstall:supportedApps.header.pixelchat.description", { appName: applicationName }),
    },
    [APPLICATIONS_ID.SecretMate]: {
      title: t("appInstall:supportedApps.header.pixelchat.title"),
      description: t("appInstall:supportedApps.header.pixelchat.description", { appName: applicationName }),
    },
  };
  return headersByApp[applicationId] ?? { title: "", description: "" };
};

const getPixelChatCardDetails = ({ t, applicationName }: { t: TFunction; applicationName: string | undefined }): ApplicationCardDetails => ({
  id: APPLICATIONS_ID.PixelChat,
  logo: "/Assets/pixelchat.svg",
  backgroundImage: "/Assets/pixelchatPhone.png",
  applicationName: APPLICATIONS_NAME.PIXELCHAT,
  title: t("appInstall:supportedApps.card.pixelchat.title", { appName: applicationName }),
  description: t("appInstall:supportedApps.card.pixelchat.description", { appName: applicationName }),
  features: [
    t("appInstall:supportedApps.card.pixelchat.feature1"),
    t("appInstall:supportedApps.card.pixelchat.feature2"),
    t("appInstall:supportedApps.card.pixelchat.feature3"),
  ],
  backgroundColor: "#1E0E2E",
  downloadLinks: [],
});

const getSpicyChatCardDetails = ({
  t,
  applicationName,
}: {
  t: TFunction;
  applicationName: string | undefined;
}): Omit<ApplicationCardDetails, "downloadLinks"> => {
  const spicychatFeature2Values = { count: "300K+" };

  return {
    id: APPLICATIONS_ID.SpicyChat,
    logo: "/Assets/spicychat.svg",
    backgroundImage: "/Assets/spicychatPhone.png",
    applicationName: APPLICATIONS_NAME.SPICYCHAT,
    title: t("appInstall:supportedApps.card.spicychat.title", { appName: applicationName }),
    description: t("appInstall:supportedApps.card.spicychat.description", { appName: applicationName }),
    features: [t("appInstall:supportedApps.card.spicychat.feature1"), t("appInstall:supportedApps.card.spicychat.feature2", spicychatFeature2Values)],
    note: t("appInstall:supportedApps.card.spicychat.note", { appName: APPLICATIONS_NAME.PIXELCHAT }),
    backgroundColor: "#001733",
  };
};

const getCurrentBrandCardDetails = ({
  applicationId,
  applicationName,
  brandingLogo,
  t,
}: {
  applicationId: string;
  applicationName: string | undefined;
  brandingLogo: string | undefined;
  t: TFunction;
}): Omit<ApplicationCardDetails, "downloadLinks"> => {
  if (applicationId === APPLICATIONS_ID.SpicyChat) {
    return getSpicyChatCardDetails({ t, applicationName });
  }

  const isPixelChat = applicationId === APPLICATIONS_ID.PixelChat;

  return {
    id: applicationId,
    logo: isPixelChat ? "/Assets/pixelchat.svg" : brandingLogo,
    backgroundImage: isPixelChat ? "/Assets/pixelchatPhone.png" : undefined,
    applicationName: applicationName ?? APPLICATIONS_NAME.SECRETMATE,
    title: t("appInstall:supportedApps.card.pixelchat.title", { appName: applicationName }),
    description: t("appInstall:supportedApps.card.pixelchat.description", { appName: applicationName }),
    features: [
      t("appInstall:supportedApps.card.pixelchat.feature1"),
      t("appInstall:supportedApps.card.pixelchat.feature2"),
      t("appInstall:supportedApps.card.pixelchat.feature3"),
    ],
    backgroundColor: "#1E0E2E",
  };
};

const getCardDetailsForBrand = ({
  brandId,
  currentApplicationId,
  applicationName,
  brandingLogo,
  t,
}: {
  brandId: string;
  currentApplicationId: string;
  applicationName: string | undefined;
  brandingLogo: string | undefined;
  t: TFunction;
}): Omit<ApplicationCardDetails, "downloadLinks"> | null => {
  if (brandId === APPLICATIONS_ID.SpicyChat) {
    return getSpicyChatCardDetails({ t, applicationName });
  }

  if (brandId === APPLICATIONS_ID.PixelChat) {
    return getPixelChatCardDetails({ t, applicationName });
  }

  if (brandId === currentApplicationId) {
    return getCurrentBrandCardDetails({
      applicationId: currentApplicationId,
      applicationName,
      brandingLogo,
      t,
    });
  }

  return null;
};

const getAndroidDownloadLink = ({
  androidApplication,
  brandId,
  isDark,
}: {
  androidApplication: IMobileApplications;
  brandId: string;
  isDark: boolean;
}): ApplicationDownloadLink => {
  let badgeImage = isDark ? AndroidDark : AndroidLight;
  if (brandId === APPLICATIONS_ID.PixelChat) {
    badgeImage = isDark ? GetItOnPlayStoreDark : GetItOnPlayStoreLight;
  }

  return {
    platform: ANDROID_PLATFORM_OS,
    badgeImage,
    redirectLink: brandId === APPLICATIONS_ID.SpicyChat ? ROUTES.androidInstallation : androidApplication.download_url,
    target: brandId === APPLICATIONS_ID.SpicyChat ? "_self" : "_blank",
  };
};

const getIosDownloadLink = ({ iosApplication, isDark }: { iosApplication: IMobileApplications; isDark: boolean }): ApplicationDownloadLink => ({
  platform: IOS_PLATFORM_OS,
  badgeImage: isDark ? AppStoreDark : AppStoreLight,
  redirectLink: iosApplication.download_url,
  target: "_blank",
});

const getDownloadLinks = ({
  androidApplication,
  brandId,
  iosApplication,
  isDark,
}: {
  androidApplication: IMobileApplications | undefined;
  brandId: string;
  iosApplication: IMobileApplications | undefined;
  isDark: boolean;
}): ApplicationDownloadLink[] => {
  const downloadLinks: ApplicationDownloadLink[] = [];

  if (androidApplication) {
    downloadLinks.push(getAndroidDownloadLink({ androidApplication, brandId, isDark }));
  }

  if (iosApplication) {
    downloadLinks.push(getIosDownloadLink({ iosApplication, isDark }));
  }

  return downloadLinks;
};

const getApplicationCardDetails = ({
  applicationId,
  applicationName,
  brandedApplications,
  brandingLogo,
  isDark,
  t,
}: {
  applicationId: string;
  applicationName: string | undefined;
  brandedApplications: BrandedMobileApplication[];
  brandingLogo: string | undefined;
  isDark: boolean;
  t: TFunction;
}): ApplicationCardDetails[] => {
  return brandedApplications.flatMap(({ androidApplication, brandId, iosApplication }) => {
    const downloadLinks = getDownloadLinks({ androidApplication, brandId, iosApplication, isDark });
    if (downloadLinks.length === 0) {
      return [];
    }

    const cardDetails = getCardDetailsForBrand({
      brandId,
      currentApplicationId: applicationId,
      applicationName,
      brandingLogo,
      t,
    });

    if (!cardDetails) {
      return [];
    }

    return [
      {
        ...cardDetails,
        downloadLinks,
      },
    ];
  });
};

export type { ApplicationCardDetails, ApplicationDownloadLink };
export { getApplicationCardDetails, getSupportedAppsHeaderDetails };
